/**
 * Effect Types for Poker Reborn Effects Engine
 *
 * Core type definitions for the effects system.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 2.1
 */

// =============================================================================
// EFFECT TYPE ENUM (P2.1.2)
// =============================================================================

/**
 * Types of effects that can be played.
 */
export enum EffectType {
  /** Immediate effect with no duration (state change, sound trigger) */
  INSTANT = 'instant',

  /** Visual animation effect (tween, sprite animation) */
  ANIMATION = 'animation',

  /** Particle system effect (sparks, confetti, smoke) */
  PARTICLE = 'particle',

  /** Audio effect (sound, music) */
  SOUND = 'sound',

  /** Continuous ambient effect (glow, pulse) */
  AMBIENT = 'ambient',

  /** Container for sequential effects */
  SEQUENCE = 'sequence',

  /** Container for parallel effects */
  PARALLEL = 'parallel',
}

// =============================================================================
// EFFECT PRIORITY ENUM (P2.1.3)
// =============================================================================

/**
 * Priority levels for effects.
 * Higher priority effects are played first and may preempt lower priority ones.
 */
export enum EffectPriority {
  /** Must play - game-critical feedback (winning hand, all-in) */
  CRITICAL = 100,

  /** Important - primary game actions (bet, raise, fold) */
  HIGH = 75,

  /** Normal - standard effects (card deals, chip moves) */
  NORMAL = 50,

  /** Low - secondary effects (highlights, indicators) */
  LOW = 25,

  /** Background - ambient/decorative (table glow, idle animations) */
  BACKGROUND = 0,
}

// =============================================================================
// EFFECT STATE ENUM (P2.1.5)
// =============================================================================

/**
 * State of an effect instance.
 */
export enum EffectState {
  /** Effect is queued but not yet started */
  PENDING = 'pending',

  /** Effect is currently playing */
  PLAYING = 'playing',

  /** Effect is paused (can be resumed) */
  PAUSED = 'paused',

  /** Effect completed successfully */
  COMPLETED = 'completed',

  /** Effect was cancelled before completion */
  CANCELLED = 'cancelled',

  /** Effect encountered an error */
  ERROR = 'error',
}

// =============================================================================
// EFFECT TARGET TYPES
// =============================================================================

/**
 * Target types that effects can be applied to.
 */
export type EffectTargetType =
  | 'seat' // Player seat (0-9)
  | 'card' // Specific card
  | 'community' // Community cards area
  | 'pot' // Pot area
  | 'table' // Entire table
  | 'dealer' // Dealer button
  | 'hero' // Hero player specifically
  | 'global'; // Global/screen-wide effect

/**
 * Target specification for an effect.
 */
export interface EffectTarget {
  /** Type of target */
  type: EffectTargetType;

  /** Optional identifier (seat number, card id, etc.) */
  id?: string | number | undefined;
}

// =============================================================================
// EFFECT RESULT (P2.1.6)
// =============================================================================

/**
 * Result of playing an effect.
 */
export interface EffectResult {
  /** Whether the effect completed successfully */
  success: boolean;

  /** Final state of the effect */
  state: EffectState;

  /** Effect ID */
  effectId: string;

  /** Actual duration in ms (may differ from planned) */
  duration: number;

  /** Error message if failed */
  error?: string | undefined;

  /** Any metadata from the effect */
  metadata?: Record<string, unknown> | undefined;
}

// =============================================================================
// EFFECT CONFIGURATION
// =============================================================================

/**
 * Configuration options for the effects system.
 */
export interface EffectsConfig {
  /** Maximum concurrent effects (default: 50) */
  maxConcurrentEffects: number;

  /** Whether to skip low priority effects when busy */
  skipLowPriorityWhenBusy: boolean;

  /** Threshold for "busy" state (% of max concurrent) */
  busyThreshold: number;

  /** Whether effects are enabled globally */
  enabled: boolean;

  /** Master volume for sound effects (0-1) */
  masterVolume: number;

  /** Animation speed multiplier (1 = normal) */
  animationSpeed: number;

  /** Whether to enable debug logging */
  debugLogging: boolean;
}

/**
 * Default effects configuration.
 */
export const DEFAULT_EFFECTS_CONFIG: EffectsConfig = {
  maxConcurrentEffects: 50,
  skipLowPriorityWhenBusy: true,
  busyThreshold: 0.8,
  enabled: true,
  masterVolume: 1.0,
  animationSpeed: 1.0,
  debugLogging: false,
};
